import { AsyncPipe, CommonModule, TitleCasePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AlertType } from '../../components/dialogs/confirm.dialog.model';
import { ColoredIconComponent } from '../../components/icons/colored-icon/colored-icon.component';
import {
  ChipColor,
  ChipComponent,
} from '../../components/inputs/chip/chip.component';
import { QuizStatus, Quiz } from '../../../models/quiz.model';
import { QuizDetailComponent } from './quiz-detail.component';

@Component({
  selector: 'app-quiz-analytics',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    TitleCasePipe,
    RouterLink,
    MomentPipe,
    ChipComponent,
    ColoredIconComponent,
    BaseChartDirective,
  ],
  templateUrl: './quiz-analytics.component.html',
  styleUrl: './quiz-detail.component.css', // Reuse parent styles
})
export class QuizAnalyticsComponent implements OnInit {
  parent = inject(QuizDetailComponent);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);
  private firestore = inject(Firestore);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  analytics = signal<any>(null);
  allAttempts = signal<any[]>([]);
  isLoading = signal(true);
  isWorking = signal(false);
  reRunAttempted = signal(false);

  chipColor = ChipColor;

  bibleBookMasteryChartData = computed<ChartData<'bar'> | null>(() => {
    const stats = this.analytics();
    return this.getBibleBookMasteryData(stats);
  });

  difficultyBubbleChartData = computed<ChartData<'bubble'> | null>(() => {
    const stats = this.analytics();
    return this.getDifficultyBubbleData(stats);
  });

  ngOnInit() {
    const groupId = this.parent.groupId;
    const quizId = this.parent.quizId;

    if (groupId && quizId) {
      this.isLoading.set(true);
      this.quizService.getQuizById({ groupId, quizId }).subscribe({
        next: (quiz) => {
          if (!quiz) {
            this.isLoading.set(false);
            return;
          }
          this.parent.myRoles$.pipe(take(1)).subscribe((roles) => {
            const isAuthorized =
              roles.management ||
              (roles.faculty && quiz.createdById === this.parent.user?.uid);

            if (!isAuthorized) {
              this.router.navigate(['../details'], {
                relativeTo: this.route,
                replaceUrl: true,
              });
              return;
            }

            this.loadAnalyticsData(groupId, quizId, quiz);
          });
        },
        error: (err) => {
          console.error('Error loading quiz details in analytics:', err);
          this.isLoading.set(false);
        },
      });
    }
  }

  private loadAnalyticsData(groupId: string, quizId: string, quiz: Quiz) {
    if (quiz.isLive) {
      const ref = doc(
        this.firestore,
        `${environment.FIRESTORE_GROUPS_COLLECTION}/${groupId}/${environment.FIRESTORE_QUIZ_REPORTS_COLLECTION}/${groupId}_${quizId}`
      );
      docData(ref).subscribe({
        next: (reportData) => {
          if (reportData) {
            const normalized = this.normalizeLiveReport(reportData, quiz);
            this.analytics.set(normalized);
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading live quiz report:', err);
          this.isLoading.set(false);
        },
      });
    } else {
      this.quizService.getQuizAnalytics(groupId, quizId).subscribe({
        next: (stats) => {
          this.analytics.set(stats);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading scheduled quiz analytics:', err);
          this.isLoading.set(false);
        },
      });

      this.quizService.getAllQuizAttempts({ groupId, quizId }).subscribe({
        next: (attempts) => {
          this.allAttempts.set(attempts);
        },
        error: (err) => {
          console.error('Error loading attempts:', err);
        },
      });
    }
  }

  private normalizeLiveReport(reportData: any, quiz: Quiz): any {
    const totalScore = quiz.totalScore || 1;
    const scoreDistribution = {
      '81-100%': 0,
      '61-80%': 0,
      '41-60%': 0,
      '21-40%': 0,
      '0-20%': 0
    };

    if (reportData.rankings) {
      reportData.rankings.forEach((r: any) => {
        const score = r.userScore ?? r.totalScore ?? 0;
        const percent = (score / totalScore) * 100;
        if (percent > 80) scoreDistribution['81-100%']++;
        else if (percent > 60) scoreDistribution['61-80%']++;
        else if (percent > 40) scoreDistribution['41-60%']++;
        else if (percent > 20) scoreDistribution['21-40%']++;
        else scoreDistribution['0-20%']++;
      });
    }

    const topAttempts = (reportData.rankings || [])
      .map((r: any) => ({
        userId: r.uid,
        userName: r.name,
        userPhotoUrl: r.photoURL || r.photoUrl || null,
        userScore: r.userScore ?? r.totalScore,
        timeTaken: r.averageTimeSeconds
      }))
      .sort((a: any, b: any) => b.userScore - a.userScore)
      .slice(0, 5);

    const mostWrongQuestions = (reportData.troubleQuestions || []).map((q: any) => ({
      questionId: q.questionId,
      questionText: q.questionText || '',
      correctCount: q.totalResponses - q.wrongCount - (q.unansweredCount || 0),
      wrongCount: q.wrongCount
    }));

    return {
      totalAttempts: reportData.summary?.participantCount || 0,
      averageScore: ((reportData.summary?.averageScore || 0) / 100) * totalScore,
      scoreDistribution,
      topAttempts,
      mostWrongQuestions,
      bibleStats: reportData.bibleStats,
      questionStats: reportData.questionStats,
      matrix: reportData.matrix
    };
  }

  reRunAnalytics() {
    const groupId = this.parent.groupId;
    const quizId = this.parent.quizId;

    if (groupId && quizId) {
      this.isWorking.set(true);
      this.quizService
        .runReportManually(groupId, quizId)
        .then((success) => {
          if (success) {
            this.quizService
              .getQuizAnalytics(groupId, quizId)
              .subscribe((stats) => {
                this.analytics.set(stats);
                this.isWorking.set(false);
                this.reRunAttempted.set(true);
                this.utilsService.onShowConfirmation({
                  title: 'Report Compiled',
                  action: 'Okay',
                  alertType: AlertType.success,
                  description:
                    'The quiz analytics report has been successfully re-compiled.',
                });
              });
          } else {
            this.isWorking.set(false);
            this.reRunAttempted.set(true);
            this.utilsService.onShowAlert(
              {
                code: 'REPORT_FAILED',
                title: 'Compilation Failed',
                message:
                  'Failed to compile the report. Please verify there are attempts recorded.',
              } as AppExceptionModel,
              false,
            );
          }
        })
        .catch((err) => {
          console.error('Failed to run manual report:', err);
          this.isWorking.set(false);
          this.reRunAttempted.set(true);
          this.utilsService.onShowAlert(
            {
              code: 'REPORT_ERROR',
              title: 'Compilation Error',
              message: err.message || 'An error occurred during compilation.',
            } as AppExceptionModel,
            false,
          );
        });
    }
  }

  get showReRunButton(): boolean {
    if (this.reRunAttempted()) return false;
    const quiz = this.parent.quiz;
    if (!quiz || quiz.quizStatus !== QuizStatus.completed || !quiz.modified) return false;
    const modifiedDate = quiz.modified.toDate
      ? quiz.modified.toDate()
      : new Date(quiz.modified as any);
    const timeDiffMs = new Date().getTime() - modifiedDate.getTime();
    const oneHourMs = 60 * 60 * 1000;
    return timeDiffMs >= oneHourMs;
  }

  getStudentPhotoUrl(userId: string): string | null {
    const attempt = this.allAttempts()?.find((a) => a.userId === userId);
    return attempt?.userPhotoUrl || null;
  }

  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  }

  barOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Success Rate (%)' },
      },
    },
  };

  bubbleOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: { display: true, text: 'Question Number' },
        ticks: { stepSize: 1 },
      },
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Difficulty (% Incorrect/Unanswered)' },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const raw = ctx.raw;
            return [
              `Q${raw.x}: ${raw.label}`,
              `Ref: ${raw.ref}`,
              `Difficulty: ${raw.y}%`,
              `Correct: ${raw.correct}`,
              `Wrong: ${raw.wrong}`,
              `Unanswered: ${raw.unanswered}`,
            ];
          },
        },
      },
    },
  };

  getBibleBookMasteryData(stats: any): ChartData<'bar'> | null {
    if (!stats?.bibleStats || stats.bibleStats.length === 0) return null;
    const labels = stats.bibleStats.map(
      (item: any) => `${item.book} ${item.chapter}`,
    );
    const data = stats.bibleStats.map((item: any) => {
      const total = item.correct + item.wrong + item.unanswered;
      return total > 0 ? Math.round((item.correct / total) * 100) : 0;
    });
    return {
      labels,
      datasets: [
        {
          label: 'Success Rate (%)',
          data,
          backgroundColor: '#6366f1', // Indigo
          borderRadius: 6,
        },
      ],
    };
  }

  getDifficultyBubbleData(stats: any): ChartData<'bubble'> | null {
    if (!stats?.questionStats || stats.questionStats.length === 0) return null;
    return {
      datasets: [
        {
          label: 'Question Performance',
          data: stats.questionStats.map((q: any, index: number) => ({
            x: index + 1,
            y: Math.round(q.difficulty * 100),
            r: Math.min(
              25,
              Math.max(6, (q.wrongCount + q.unansweredCount) * 2),
            ),
            label: q.questionText,
            ref: `${q.book} ${q.chapter}:${q.verse}`,
            correct: q.correctCount,
            wrong: q.wrongCount,
            unanswered: q.unansweredCount,
          })),
          backgroundColor: 'rgba(239, 68, 68, 0.6)',
          borderColor: '#ef4444',
          borderWidth: 1,
        },
      ],
    };
  }
}
